import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { ArrowLeft, ArrowRight, CheckCircle, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import docsContent from './docsContent';

/* --- Small styled browser frame used by mockup sections --- */
const MockFrame = ({ url, children }) => (
  <div className="mt-6 rounded-xl overflow-hidden border border-gray-200 shadow-md bg-white">
    <div className="bg-gradient-to-r from-slate-800 to-slate-700 px-4 py-2.5 flex items-center gap-3">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
        <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
      </div>
      <div className="flex-1 bg-slate-600/60 rounded px-3 py-1 text-[11px] text-slate-200 font-mono truncate">
        {url}
      </div>
    </div>
    <div className="bg-slate-50">{children}</div>
  </div>
);

const FakeField = ({ label, value }) => (
  <div>
    <label className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</label>
    <div className="mt-1 border border-gray-200 rounded-md px-3 py-2 text-sm text-gray-800 bg-white">{value}</div>
  </div>
);

const toneMap = {
  blue: 'bg-blue-50 border-blue-200 text-blue-800',
  amber: 'bg-amber-50 border-amber-200 text-amber-800',
  green: 'bg-green-50 border-green-200 text-green-800',
};

/* --- Section renderers --- */
const Section = ({ section }) => {
  switch (section.type) {
    case 'intro':
      return <p className="text-gray-700 leading-relaxed">{section.body}</p>;

    case 'steps':
      return (
        <div>
          {section.title && <h3 className="text-lg font-bold text-gray-900 mb-4">{section.title}</h3>}
          <div className="space-y-5">
            {section.items.map((s, i) => (
              <div key={i} className="flex gap-4">
                <div className="w-8 h-8 bg-gradient-to-br from-primary to-cyan-500 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">{s.title}</h4>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'list':
      return (
        <div>
          {section.title && <h3 className="text-lg font-bold text-gray-900 mb-3">{section.title}</h3>}
          <ul className="space-y-2">
            {section.items.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-gray-700 text-sm">
                <CheckCircle className="text-green-500 mt-0.5 flex-shrink-0" size={16} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'bullets':
      return (
        <div>
          {section.title && <h3 className="text-lg font-bold text-gray-900 mb-4">{section.title}</h3>}
          <ul className="space-y-3">
            {section.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-gray-900 text-sm">{item.title}</div>
                  <div className="text-gray-600 text-sm mt-0.5">{item.body}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      );

    case 'tip':
      return (
        <div className={`border rounded-lg p-4 text-sm ${toneMap[section.tone || 'blue']}`}>
          <strong>Tip:</strong> {section.body}
        </div>
      );

    case 'troubleshoot':
      return (
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Troubleshooting</h3>
          <div className="space-y-4">
            {section.items.map((t, i) => (
              <div key={i}>
                <h4 className="font-semibold text-gray-900 mb-1 text-sm">{t.q}</h4>
                <p className="text-gray-600 text-sm leading-relaxed">{t.a}</p>
              </div>
            ))}
          </div>
        </div>
      );

    case 'formMock':
      return (
        <MockFrame url={section.url}>
          <div className="p-6 max-w-md mx-auto">
            <div className="text-center mb-4">
              <h4 className="font-bold text-gray-900">{section.title}</h4>
              {section.subtitle && <p className="text-xs text-gray-500 mt-1">{section.subtitle}</p>}
            </div>
            <div className="space-y-3">
              {section.fields.map((f, i) => (
                <FakeField key={i} label={f.label} value={f.value} />
              ))}
            </div>
          </div>
        </MockFrame>
      );

    case 'panelMock':
      return (
        <MockFrame url={section.url}>
          <div className="p-6">
            {section.title && <h4 className="font-semibold text-gray-900 mb-3">{section.title}</h4>}
            <div className="divide-y divide-gray-100">
              {section.rows.map((r, i) => (
                <div key={i} className="py-2 flex justify-between text-sm">
                  <span className="text-gray-500">{r.label}</span>
                  <span className="text-gray-800 font-medium">{r.value}</span>
                </div>
              ))}
            </div>
          </div>
        </MockFrame>
      );

    default:
      return null;
  }
};

const GenericDocPage = () => {
  const { slug } = useParams();
  const doc = docsContent[slug];

  if (!doc) {
    // Fallback: if slug isn't defined in content, send them back to the docs index.
    return <Navigate to="/docs" replace />;
  }

  // Resolve the icon by name from lucide-react (fallback to FileText)
  const Icon = Icons[doc.iconName] || Icons.FileText;

  // Related pages — resolve titles from content map, only show ones that exist
  const related = (doc.related || [])
    .map(s => {
      if (s === 'quick-start') return { slug: s, title: 'Quick Start Guide' };
      if (s === 'google-calendar') return { slug: s, title: 'Google Calendar Integration' };
      const d = docsContent[s];
      return d ? { slug: s, title: d.title } : null;
    })
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-gray-50" data-testid={`doc-page-${slug}`}>
      <SEO
        title={`${doc.title} - MoveMate Documentation`}
        description={doc.description}
        url={`https://movemate.tech/docs/${slug}`}
      />

      {/* Header */}
      <header className="bg-gradient-to-r from-primary to-cyan-500 text-white py-10 px-4">
        <div className="max-w-4xl mx-auto">
          <Link to="/docs" className="inline-flex items-center text-white/80 hover:text-white mb-4 transition" data-testid="back-to-docs-link">
            <ArrowLeft size={20} className="mr-2" />
            Back to Documentation
          </Link>
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <Icon size={32} />
            <span className="text-sm bg-white/20 px-3 py-1 rounded-full">{doc.category}</span>
            {doc.readTime && (
              <span className="text-sm bg-white/10 px-3 py-1 rounded-full flex items-center gap-1.5">
                <Clock size={14} /> {doc.readTime} read
              </span>
            )}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold">{doc.title}</h1>
          <p className="text-cyan-100 mt-3 max-w-2xl">{doc.description}</p>
        </div>
      </header>

      {/* Sections */}
      <main className="max-w-4xl mx-auto py-12 px-4">
        <div className="space-y-6">
          {doc.sections.map((s, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm p-6 sm:p-8">
              <Section section={s} />
            </div>
          ))}
        </div>

        {/* Related */}
        {related.length > 0 && (
          <div className="bg-gradient-to-r from-primary/10 to-cyan-500/10 rounded-xl p-6 sm:p-8 mt-8" data-testid="related-section">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Related Guides</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {related.map(r => (
                <Link
                  key={r.slug}
                  to={`/docs/${r.slug}`}
                  className="bg-white p-4 rounded-lg hover:shadow-md transition flex items-center justify-between border border-transparent hover:border-primary/20"
                  data-testid={`related-${r.slug}`}
                >
                  <span className="font-medium text-gray-900 text-sm">{r.title}</span>
                  <ArrowRight size={18} className="text-primary flex-shrink-0" />
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Bottom nav */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-8">
          <Link to="/docs" className="text-primary hover:underline flex items-center gap-2 font-medium">
            <ArrowLeft size={20} />
            Back to Documentation
          </Link>
          <Link to="/contact">
            <Button variant="outline">Need Help? Contact Support</Button>
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-white py-8 px-4 mt-12">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-500 text-sm">
            Need help? <Link to="/contact" className="text-primary hover:underline font-medium">Contact Support</Link>
            {' '}or email <a href="mailto:info@movemate.tech" className="text-primary hover:underline">info@movemate.tech</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default GenericDocPage;
